import { useCallback } from 'react'
import { useMutation } from '@apollo/client/react'
import { AdminAPI, GraphQL } from '@guestgoodie/graphql'
import { useLingui } from '@lingui/react/macro'

import * as Form from '@/features/form'

export type FormData = {
  title: string
  firstName: string
  lastName: string
  email: string
  gender: GraphQL.Gender
}

interface Options {
  id?: number
  onDone?: () => void
}

export default function useGuestForm({ id, onDone }: Options) {
  const { t } = useLingui()

  const [createGuest] = useMutation(AdminAPI.guest.CREATE, {
    refetchQueries: [
      AdminAPI.guest.QUERY,
      AdminAPI.stay.GET,
      AdminAPI.stay.GET_BY_TOKEN
    ]
  })

  const [updateGuest] = useMutation(AdminAPI.guest.UPDATE, {
    update(cache, { data }) {
      if (!data) return

      // Write the raw mutation result to the GET query cache
      if (id) {
        cache.writeQuery({
          query: AdminAPI.guest.GET,
          variables: {
            id
          },
          data: {
            getGuest: data.updateGuest
          }
        })
      }
    },
    // Keep refetching stay queries as guest data is embedded in stay
    refetchQueries: [AdminAPI.stay.GET, AdminAPI.stay.GET_BY_TOKEN]
  })

  const { errors, handlePromiseCatch } = Form.useErrorHelpers({
    mapper(field, message) {
      if (field === 'email' && message === 'has already been taken') {
        return [
          field,
          {
            type: message,
            message: t`A guest with this email exists already.`
          }
        ]
      }
    }
  })

  const create = useCallback(
    (data: FormData) => {
      return createGuest({
        variables: {
          input: data
        }
      })
        .then(({ data, error }) => {
          if (error) return Promise.reject(error)
          if (!data) return Promise.reject('unknown')

          onDone?.()
        })
        .catch(error => handlePromiseCatch(error))
    },
    [onDone, createGuest, handlePromiseCatch]
  )

  const update = useCallback(
    (data: FormData) => {
      if (!id) return Promise.resolve()

      return updateGuest({
        variables: {
          input: {
            id,
            ...data
          }
        }
      })
        .then(({ data, error }) => {
          if (error) return Promise.reject(error)
          if (!data) return Promise.reject('unknown')

          onDone?.()
        })
        .catch(error => handlePromiseCatch(error))
    },
    [id, onDone, updateGuest, handlePromiseCatch]
  )

  return {
    errors,
    create,
    update
  }
}
